import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Typography, Statistic, Spin, Alert, Button, Space, Tabs, Collapse, Input } from 'antd';
import { 
  UserOutlined, 
  ProjectOutlined, 
  ClockCircleOutlined, 
  TeamOutlined,
  TrophyOutlined,
  ExclamationCircleOutlined,
  RiseOutlined,
  FallOutlined,
  DashboardOutlined,
  BarChartOutlined,
  UserSwitchOutlined,
  SettingOutlined
} from '@ant-design/icons';
import { useAdminStore } from '../../store/useAdminStore';
import { 
  DonutChart, 
  BarChartComponent, 
  LineChartComponent,
  ProjectProgressCard,
  EmployeePerformanceCard,
  MetricCard
} from '../common/Charts';
import apiService from '../../services/apiService';
import { useNavigate } from'react-router-dom';

const { Title, Text } = Typography;
const { Panel } = Collapse;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { employees, projects, fetchEmployees, fetchProjects } = useAdminStore();
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState(false);
  const [error, setError] = useState(null);
  const [projectSearch, setProjectSearch] = useState('');
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [managerSearch, setManagerSearch] = useState('');
  const [adminSearch, setAdminSearch] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [refreshInterval, setRefreshInterval] = useState(null);

  // Add custom styles for the dashboard with sidebar
  const tabStyles = `
    .admin-dashboard-layout {
      display: flex;
      gap: 24px;
      min-height: calc(100vh - 200px);
    }
    .admin-main-content {
      flex: 1;
      min-width: 0;
    }
    .admin-sidebar {
      width: 320px;
      flex-shrink: 0;
      position: sticky;
      top: 20px;
      height: fit-content;
    }
    .admin-dashboard-tabs .ant-tabs-tab {
      border-radius: 8px 8px 0 0 !important;
      margin: 0 4px !important;
      background: #f1f5f9 !important;
      border: 1px solid #e2e8f0 !important;
    }
    .admin-dashboard-tabs .ant-tabs-tab-active {
      background: #ffffff !important;
      border-bottom: 2px solid #2563eb !important;
    }
    .admin-dashboard-tabs .ant-tabs-content-holder {
      background: #ffffff;
      border-radius: 0 0 8px 8px;
      border: 1px solid #e2e8f0;
      border-top: none;
    }
    .admin-dashboard-tabs .ant-collapse {
      border-radius: 8px;
      overflow: hidden;
    }
    .admin-dashboard-tabs .ant-collapse-item:last-child {
      border-radius: 0 0 8px 8px;
    }
    .recent-activity-card {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      border-radius: 12px;
      border: none;
      box-shadow: 0 4px 20px rgba(0,0,0,0.1);
    }
    .recent-activity-card .ant-card-head {
      border-bottom: 1px solid rgba(255,255,255,0.2);
    }
    .recent-activity-card .ant-card-head-title {
      color: white !important;
    }
    .activity-item {
      padding: 12px;
      background: rgba(255,255,255,0.1);
      border-radius: 8px;
      margin-bottom: 8px;
      backdrop-filter: blur(10px);
      border: 1px solid rgba(255,255,255,0.2);
    }
    .activity-item:last-child {
      margin-bottom: 0;
    }
    .recent-activity-list {
      max-height: 260px; /* ~3 items visible */
      overflow-y: auto;
      padding-right: 6px;
    }
    .recent-activity-list::-webkit-scrollbar {
      width: 6px;
    }
    .recent-activity-list::-webkit-scrollbar-thumb {
      background: rgba(255,255,255,0.3);
      border-radius: 9999px;
    }
    .bento-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 10px;
    }
    .bento-tile {
      border-radius: 12px;
      padding: 14px;
      background: #ffffff;
      border: 1px solid #e2e8f0;
      box-shadow: 0 1px 2px rgba(0,0,0,0.04);
    }
    @media (max-width: 1200px) {
      .admin-dashboard-layout {
        flex-direction: column;
      }
      .admin-sidebar {
        width: 100%;
        position: static;
      }
    }
  `;

  useEffect(() => {
    loadDashboardData();
    
    // Set up auto-refresh every 30 seconds
    const interval = setInterval(() => {
      loadDashboardData(true); // Silent refresh
    }, 30000);
    
    setRefreshInterval(interval);
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, []);
  
  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [refreshInterval]);

  const loadDashboardData = async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      setError(null);
      
      // Fetch basic data
      await Promise.all([
        fetchEmployees(),
        fetchProjects()
      ]);
      
      // Fetch analytics data
      const response = await apiService.get('/api/analytics/dashboard');
      
      if (response && response.success && response.data) {
        setAnalyticsData(response.data);
      } else if (response && response.data) {
        // Handle case where response doesn't have success flag but has data
        setAnalyticsData(response.data);
      } else {
        console.error('Invalid analytics response structure:', response);
        setAnalyticsData(null);
      }
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      if (!silent) {
        setError('Failed to load dashboard data. Please try again.');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };
  
  const handleTabChange = async (key) => {
    setActiveTab(key);
    setTabLoading(true);
    
    try {
      // Refresh data when switching tabs
      await loadDashboardData(true);
      
      // Tab-specific data loading if needed
      switch (key) {
        case 'projects':
          await fetchProjects();
          break;
        case 'employees':
          await fetchEmployees();
          break;
        default:
          break;
      }
    } catch (err) {
      console.error('Failed to load tab data:', err);
    } finally {
      setTabLoading(false);
    }
  };

  const activeEmployees = Array.isArray(employees) ? employees.filter(emp => emp.isActive && emp.role === 'employee').length : 0;
  const activeProjects = Array.isArray(projects) ? projects.filter(proj => proj.isActive).length : 0;
  

  // Prepare chart data - Updated for timesheet-based analytics
  const timesheetStatusData = analyticsData?.timesheetStatusStats?.map(stat => ({
    name: stat._id.charAt(0).toUpperCase() + stat._id.slice(1),
    value: stat.count
  })) || [];

  const priorityData = analyticsData?.priorityStats?.map(stat => ({
    name: stat._id.charAt(0).toUpperCase() + stat._id.slice(1),
    value: stat.count
  })) || [];

  const monthlyTrendData = analyticsData?.monthlyTrend?.map(item => ({
    month: `${item._id.year}-${String(item._id.month).padStart(2, '0')}`,
    timesheets: item.count,
    hours: item.totalHours || 0
  })) || [];

  const topProjects = analyticsData?.projectStats || [];
  const topEmployees = analyticsData?.employeeStats || [];

  // Filter to show only actual employees (not admins or project managers)
  const filteredEmployees = Array.isArray(employees) 
    ? employees.filter(emp => emp.isActive && emp.role === 'employee')
    : [];
  
  // Get all active employees for project manager filtering
  const allActiveEmployees = Array.isArray(employees) 
    ? employees.filter(emp => emp.isActive)
    : [];
  

  // Merge analytics stats into store data for richer cards - Use timesheet-based data
  const mergedProjects = (Array.isArray(projects) ? projects : []).map(p => {
    const stat = (analyticsData?.projectStats || []).find(ps => ps.name === p.name);
    return {
      _id: p._id,
      name: p.name,
      totalHours: stat?.totalHours || 0,
      totalAllocatedHours: stat?.totalAllocatedHours || 0,
      totalTimesheets: stat?.totalTimesheets || 0,
      approvedTimesheets: stat?.approvedTimesheets || 0,
      pendingTimesheets: stat?.pendingTimesheets || 0,
      teamSize: stat?.teamSize || 0,
      completionRate: stat?.completionRate || 0,
      // Keep backward compatibility
      totalTasks: stat?.totalTimesheets || 0,
      completedTasks: stat?.approvedTimesheets || 0,
      inProgressTasks: stat?.pendingTimesheets || 0,
    };
  });

  const mergedEmployees = filteredEmployees.map(e => {
    const stat = (analyticsData?.employeeStats || []).find(es => es.email === e.email || es.name === e.name);
    return {
      _id: e._id,
      name: e.name,
      email: e.email,
      role: e.role,
      position: e.position,
      department: e.department,
      totalHours: stat?.totalHours || 0,
      totalTimesheets: stat?.totalTimesheets || 0,
      approvedTimesheets: stat?.approvedTimesheets || 0,
      pendingTimesheets: stat?.pendingTimesheets || 0,
      projectsWorkedOn: stat?.projectsWorkedOn || 0,
      avgHoursPerTimesheet: stat?.avgHoursPerTimesheet || 0,
      allocationPercentage: stat?.allocationPercentage || 0,
      // Keep backward compatibility
      totalTasks: stat?.totalTimesheets || 0,
      completedTasks: stat?.approvedTimesheets || 0,
      avgHoursPerTask: stat?.avgHoursPerTimesheet || 0,
    };
  });

  // Create merged data for all active employees (including managers and admins)
  const mergedAllEmployees = allActiveEmployees.map(e => {
    const stat = (analyticsData?.employeeStats || []).find(es => es.email === e.email || es.name === e.name);
    return {
      _id: e._id,
      name: e.name,
      email: e.email,
      role: e.role,
      position: e.position,
      department: e.department,
      totalHours: stat?.totalHours || 0,
      totalTimesheets: stat?.totalTimesheets || 0,
      approvedTimesheets: stat?.approvedTimesheets || 0,
      pendingTimesheets: stat?.pendingTimesheets || 0,
      projectsWorkedOn: stat?.projectsWorkedOn || 0,
      avgHoursPerTimesheet: stat?.avgHoursPerTimesheet || 0,
      allocationPercentage: stat?.allocationPercentage || 0,
      // Keep backward compatibility
      totalTasks: stat?.totalTimesheets || 0,
      completedTasks: stat?.approvedTimesheets || 0,
      avgHoursPerTask: stat?.avgHoursPerTimesheet || 0,
    };
  });

  // Apply search filters (case-insensitive)
  const filteredProjectsForView = mergedProjects.filter(p =>
    !projectSearch || (p.name || '').toLowerCase().includes(projectSearch.toLowerCase())
  );

  const filteredEmployeesForView = mergedEmployees.filter(e => {
    const q = employeeSearch.toLowerCase();
    return (
      !employeeSearch ||
      (e.name || '').toLowerCase().includes(q) ||
      (e.email || '').toLowerCase().includes(q)
    );
  });

  // Filter project managers from all employees - include all roles that can manage projects
  const projectManagers = mergedAllEmployees.filter(e => 
    e.role === 'project_manager' || 
    e.position?.toLowerCase().includes('manager') ||
    e.position?.toLowerCase().includes('lead')
  );
  
  // Filter admin users from all employees
  const adminUsers = mergedAllEmployees.filter(e => 
    e.role === 'admin'
  );
  
  
  const filteredManagersForView = projectManagers.filter(m => {
    const q = managerSearch.toLowerCase();
    return (
      !managerSearch ||
      (m.name || '').toLowerCase().includes(q) ||
      (m.email || '').toLowerCase().includes(q)
    );
  });

  const filteredAdminsForView = adminUsers.filter(a => {
    const q = adminSearch.toLowerCase();
    return (
      !adminSearch ||
      (a.name || '').toLowerCase().includes(q) ||
      (a.email || '').toLowerCase().includes(q)
    );
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spin size="large" />
      </div>
    );
  }
  console.log(priorityData,"priorityData");
  

  return (
    <div className="p-6">
      <style dangerouslySetInnerHTML={{ __html: tabStyles }} />
      
      <div className="flex justify-between items-center mb-6">
        <Title level={2} className="mb-0">Admin Dashboard</Title>
        <Space>
          <Button onClick={() => loadDashboardData()} loading={loading}>
            Refresh Data
          </Button>
          <Text type="secondary" className="text-sm">
            Auto-refresh: 30s
          </Text>
        </Space>
      </div>

      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          className="mb-6"
        />
      )}

      <div className="admin-dashboard-layout">
        {/* Main Content */}
        <div className="admin-main-content">
          <Tabs 
            activeKey={activeTab}
            onChange={handleTabChange}
            size="large" 
            className="admin-dashboard-tabs"
            tabBarStyle={{
              background: '#f8fafc',
              padding: '12px 24px',
              marginBottom: 0,
              borderRadius: '8px 8px 0 0'
            }}
            type="card"
            items={[
              {
                key: 'overview',
                label: (
                  <span>
                    <DashboardOutlined />
                    Overview
                    {tabLoading && activeTab === 'overview' && <Spin size="small" className="ml-2" />}
                  </span>
                ),
                children: (
                  <div className="space-y-6">
                    {/* Key Metrics - Timesheet Based */}
                    <Card title="Timesheet Analytics Overview" className="shadow-sm">
                      <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} lg={6}>
                          <MetricCard
                            title="Total Hours Logged"
                            value={Math.round(analyticsData?.overview?.totalHoursLogged || 0)}
                            prefix={<ClockCircleOutlined />}
                            suffix="h"
                            color="#52c41a"
                          />
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                          <MetricCard
                            title="Total Timesheets"
                            value={analyticsData?.overview?.totalTimesheets || 0}
                            prefix={<ProjectOutlined />}
                            color="#1890ff"
                          />
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                          <MetricCard
                            title="Active Projects"
                            value={activeProjects}
                            prefix={<TeamOutlined />}
                            color="#722ed1"
                          />
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                          <MetricCard
                            title="Overdue Timesheets"
                            value={analyticsData?.overview?.overdueTimesheets || 0}
                            prefix={<ExclamationCircleOutlined />}
                            color="#f5222d"
                          />
                        </Col>
                      </Row>
                      
                      {/* Secondary Metrics Row */}
                      <Row gutter={[16, 16]} className="mt-4">
                        <Col xs={24} sm={12} lg={6}>
                          <MetricCard
                            title="Active Employees"
                            value={activeEmployees}
                            prefix={<UserOutlined />}
                            color="#13c2c2"
                          />
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                          <MetricCard
                            title="Approved Timesheets"
                            value={analyticsData?.timesheetStatusStats?.find(s => s._id === 'approved')?.count || 0}
                            prefix={<TrophyOutlined />}
                            color="#52c41a"
                          />
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                          <MetricCard
                            title="Pending Timesheets"
                            value={analyticsData?.timesheetStatusStats?.find(s => s._id === 'submitted')?.count || 0}
                            prefix={<RiseOutlined />}
                            color="#faad14"
                          />
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                          <MetricCard
                            title="Avg Hours/Timesheet"
                            value={analyticsData?.overview?.totalTimesheets > 0 
                              ? Math.round((analyticsData?.overview?.totalHoursLogged / analyticsData?.overview?.totalTimesheets) * 10) / 10 
                              : 0}
                            prefix={<BarChartOutlined />}
                            suffix="h"
                            color="#eb2f96"
                          />
                        </Col>
                      </Row>
                    </Card>

                    {/* Analytics Charts */}
                    <Collapse defaultActiveKey={['charts']} className="bg-white">
                      <Panel 
                        header={
                          <span className="flex items-center">
                            <BarChartOutlined className="mr-2" />
                            Analytics & Trends
                          </span>
                        } 
                        key="charts"
                      >
                        <Row gutter={[16, 16]}>
                          <Col xs={24} lg={8}>
                            <DonutChart
                              data={timesheetStatusData}
                              title="Timesheet Status Distribution"
                              height={300}
                            />
                          </Col>
                          <Col xs={24} lg={8}>
                            <DonutChart
                              data={priorityData}
                              title="Task Priority Distribution"
                              height={300}
                            />
                          </Col>
                          <Col xs={24} lg={8}>
                            <LineChartComponent
                              data={monthlyTrendData}
                              title="Timesheet Submission Trend"
                              xKey="month"
                              yKey="timesheets"
                              height={300}
                            />
                          </Col>
                        </Row>
                      </Panel>
                    </Collapse>
                  </div>
                )
              },
              {
                key: 'projects',
                label: (
                  <span>
                    <ProjectOutlined />
                    Projects
                    {tabLoading && activeTab === 'projects' && <Spin size="small" className="ml-2" />}
                  </span>
                ),
                children: (
                  <div className="space-y-6">
                    <Card 
                      title="Project Performance Overview" 
                      className="shadow-sm"
                      extra={
                        <Input.Search
                          allowClear
                          placeholder="Search projects..."
                          value={projectSearch}
                          onChange={(e) => setProjectSearch(e.target.value)}
                          style={{ maxWidth: 320 }}
                        />
                      }
                    >
                      <Row gutter={[16, 16]}>
                        {filteredProjectsForView.map((project, index) => (
                          <Col xs={24} sm={12} lg={8} key={project._id || index}>
                            <ProjectProgressCard project={project} />
                          </Col>
                        ))}
                      </Row>
                    </Card>
                  </div>
                )
              },
              {
                key: 'employees',
                label: (
                  <span>
                    <UserSwitchOutlined />
                    Employees
                    {tabLoading && activeTab === 'employees' && <Spin size="small" className="ml-2" />}
                  </span>
                ),
                children: (
                  <div className="space-y-6">
                    <Card 
                      title="Employee Performance Overview" 
                      className="shadow-sm"
                      extra={
                        <Input.Search
                          allowClear
                          placeholder="Search employees by name or email..."
                          value={employeeSearch}
                          onChange={(e) => setEmployeeSearch(e.target.value)}
                          style={{ maxWidth: 360 }}
                        />
                      }
                    >
                      <Row gutter={[16, 16]}>
                        {filteredEmployeesForView.map((employee, index) => (
                          <Col xs={24} sm={12} lg={8} key={employee._id || index}>
                            <EmployeePerformanceCard employee={employee} />
                          </Col>
                        ))}
                      </Row>
                    </Card>
                  </div>
                )
              },
              {
                key: 'managers',
                label: (
                  <span>
                    <TeamOutlined />
                    Project Managers
                    {tabLoading && activeTab === 'managers' && <Spin size="small" className="ml-2" />}
                  </span>
                ),
                children: (
                  <div className="space-y-6">
                    <Card 
                      title="Project Manager Performance Overview" 
                      className="shadow-sm"
                      extra={
                        <Input.Search
                          allowClear
                          placeholder="Search managers by name or email..."
                          value={managerSearch}
                          onChange={(e) => setManagerSearch(e.target.value)}
                          style={{ maxWidth: 360 }}
                        />
                      }
                    >
                      <Row gutter={[16, 16]}>
                        {filteredManagersForView.map((manager, index) => (
                          <Col xs={24} sm={12} lg={8} key={manager._id || index}>
                            <EmployeePerformanceCard employee={manager} isManager={true} />
                          </Col>
                        ))}
                      </Row>
                    </Card>
                  </div>
                )
              },
              {
                key: 'admins',
                label: (
                  <span>
                    <SettingOutlined />
                    Admins
                    {tabLoading && activeTab === 'admins' && <Spin size="small" className="ml-2" />}
                  </span>
                ),
                children: (
                  <div className="space-y-6">
                    <Card 
                      title="Admin Performance Overview" 
                      className="shadow-sm"
                      extra={
                        <Input.Search
                          allowClear
                          placeholder="Search admins by name or email..."
                          value={adminSearch}
                          onChange={(e) => setAdminSearch(e.target.value)}
                          style={{ maxWidth: 360 }}
                        />
                      }
                    >
                      <Row gutter={[16, 16]}>
                        {filteredAdminsForView.map((admin, index) => (
                          <Col xs={24} sm={12} lg={8} key={admin._id || index}>
                            <EmployeePerformanceCard employee={admin} isManager={true} />
                          </Col>
                        ))}
                      </Row>
                    </Card>
                  </div>
                )
              }
            ]}
          />
        </div>

        {/* Right Sidebar */}
        <div className="admin-sidebar">
          <Card 
            title={
              <span className="flex items-center">
                <ClockCircleOutlined className="mr-2" />
                Recent Activity
              </span>
            } 
            className="recent-activity-card"
            size="small"
          >
            <div className="recent-activity-list space-y-2">
              {analyticsData?.recentTasks?.map((task, index) => (
                <div key={task._id || index} className="activity-item">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mr-3">
                      {task.status === 'completed' ? (
                        <TrophyOutlined className="text-green-400 text-lg" />
                      ) : task.status === 'in-progress' ? (
                        <RiseOutlined className="text-blue-400 text-lg" />
                      ) : (
                        <ClockCircleOutlined className="text-orange-400 text-lg" />
                      )}
                    </div>
                    <div className="flex-grow min-w-0">
                      <p className="font-medium text-sm mb-1 text-white truncate">{task.title}</p>
                      <p className="text-gray-200 text-xs truncate">
                        {task.projectName} • {task.assignedToName}
                      </p>
                      <p className="text-gray-300 text-xs mt-1">
                        {new Date(task.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              )) || (
                <div className="text-center text-gray-200 py-8">
                  <ClockCircleOutlined className="text-2xl mb-2 block" />
                  No recent activity
                </div>
              )}
            </div>
          </Card>

          {/* Bento Box Quick Actions */}
          <div className="mt-4">
            <Title level={5} className="mb-3">Quick Actions</Title>
            <div className="bento-grid">
              <div className="bento-tile">
                <Text strong className="block mb-1">Add Employee</Text>
                <Text type="secondary" className="block mb-2">Create a new employee account</Text>
                <Button type="primary" size="small" onClick={() => navigate('/admin/employees', { state: { from: 'dashboard'} })}>Add</Button>
              </div>
              <div className="bento-tile">
                <Text strong className="block mb-1">View Employees</Text>
                <Text type="secondary" className="block mb-2">Manage all employees</Text>
                <Button size="small" onClick={() => navigate('/admin/employees')}>Open</Button>
              </div>
              <div className="bento-tile">
                <Text strong className="block mb-1">Add Project</Text>
                <Text type="secondary" className="block mb-2">Start a new project</Text>
                <Button type="primary" size="small" onClick={() => navigate('/admin/projects', { state: { from: 'dashboard'} })}>Add</Button>
              </div>
              <div className="bento-tile">
                <Text strong className="block mb-1">View Projects</Text>
                <Text type="secondary" className="block mb-2">Manage all projects</Text>
                <Button size="small" onClick={() => navigate('/admin/projects')}>Open</Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
